import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BellOff, Plus, Trash2 } from 'lucide-react';
import {
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorNote,
  Field,
  Input,
  Skeleton,
} from '../../components/ui';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { errorMessage, useToast } from '../../lib/toast';
import type { BlacklistEntry, Page } from '../../lib/types';

const PAGE_SIZE = 50;

/**
 * The do-not-disturb list.
 *
 * Keyed by device id, and enforced inside the one service every push in the
 * backend passes through — campaigns, automations and gift/trial grants alike
 * — so a device listed here is silenced everywhere at once rather than per
 * feature.
 */
export function BlacklistTab() {
  const [search, setSearch] = useState('');

  const entries = useQuery({
    queryKey: ['blacklist', search],
    queryFn: () =>
      api.get<Page<BlacklistEntry>>('/api/notifications/blacklist', {
        limit: PAGE_SIZE,
        search: search.trim() || undefined,
      }),
  });

  return (
    <div className="space-y-4">
      <AddForm />

      <Card>
        <CardHeader
          title="Silenced devices"
          description="Checked on every send. Nothing in this backend can push to a device on this list."
        />

        <div className="border-b border-line p-3 sm:p-4">
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by device id (starts with…)"
            aria-label="Search the blacklist"
          />
        </div>

        {entries.isError && (
          <div className="p-4">
            <ErrorNote error={entries.error} />
          </div>
        )}

        {entries.isPending ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 4 }, (_, index) => (
              <Skeleton key={index} className="h-12" />
            ))}
          </div>
        ) : entries.data && entries.data.items.length > 0 ? (
          <ul className="divide-y divide-line">
            {entries.data.items.map((entry) => (
              <EntryRow key={entry.id} entry={entry} />
            ))}
          </ul>
        ) : (
          <EmptyState
            icon={<BellOff size={28} />}
            title={search ? 'No match' : 'Nobody is silenced'}
            description={
              search
                ? 'No silenced device starts with that id.'
                : 'Add a device id above to stop every notification reaching it.'
            }
          />
        )}

        {entries.data?.has_more && (
          <p className="border-t border-line px-4 py-2.5 text-xs text-ink-3 sm:px-5">
            Showing the {PAGE_SIZE} most recent. Search by device id to find an
            older entry.
          </p>
        )}
      </Card>
    </div>
  );
}

function AddForm() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [deviceId, setDeviceId] = useState('');
  const [reason, setReason] = useState('');

  const add = useMutation({
    mutationFn: () =>
      api.post<BlacklistEntry>('/api/notifications/blacklist', {
        device_id: deviceId.trim(),
        reason: reason.trim() || undefined,
      }),
    onSuccess: () => {
      toast.success('Device silenced');
      setDeviceId('');
      setReason('');
      void queryClient.invalidateQueries({ queryKey: ['blacklist'] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <Card>
      <CardHeader title="Silence a device" />
      <div className="flex flex-wrap items-end gap-2 p-4 sm:p-5">
        <Field label="Device id" className="min-w-[240px] flex-1">
          <Input
            value={deviceId}
            onChange={(event) => setDeviceId(event.target.value)}
            placeholder="Paste a device id"
          />
        </Field>
        <Field
          label="Reason"
          hint="Optional — a note for whoever reads this later"
          className="min-w-[200px] flex-1"
        >
          <Input
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Asked to stop"
          />
        </Field>
        <Button
          variant="primary"
          icon={<Plus size={16} />}
          disabled={!deviceId.trim()}
          loading={add.isPending}
          onClick={() => add.mutate()}
        >
          Add
        </Button>
      </div>
    </Card>
  );
}

function EntryRow({ entry }: { entry: BlacklistEntry }) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const remove = useMutation({
    mutationFn: () =>
      api.delete<void>(
        `/api/notifications/blacklist/${encodeURIComponent(entry.device_id)}`,
      ),
    onSuccess: () => {
      toast.success('Device can receive notifications again');
      void queryClient.invalidateQueries({ queryKey: ['blacklist'] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <li className="flex items-start justify-between gap-3 px-4 py-3 sm:px-5">
      <div className="min-w-0">
        <p className="break-anywhere text-sm font-medium text-ink">
          {entry.device_id}
        </p>
        <p className="mt-0.5 text-xs text-ink-3">
          {entry.reason || 'No reason given'} · added{' '}
          {formatDateTime(entry.created_at)}
        </p>
      </div>
      <Button
        variant="ghost"
        aria-label={`Unsilence ${entry.device_id}`}
        loading={remove.isPending}
        onClick={() => {
          if (confirm(`Let ${entry.device_id} receive notifications again?`)) {
            remove.mutate();
          }
        }}
      >
        <Trash2 size={15} className="text-danger" />
      </Button>
    </li>
  );
}
